import React, { useState, useEffect, useRef } from 'react';
// 🟡 CHECK-IN PREVIEW (v6.460): πριν την ημέρα check-in τα consents δεν γράφονται στη DB.
import { isCheckinPreview } from '../utils/previewMode';

type BoxType = 'checkbox' | 'file_upload' | 'modal_warning';

interface BoxData {
  id: number;
  box_key: string;
  page_group: string;
  box_type: BoxType;
  style_color: string;
  required: boolean;
  active: boolean;
  sort_order: number;
  [key: string]: any; // 40 lang columns: title_xx, description_xx, checkbox_label_xx, button_label_xx
}

interface DynamicBoxProps {
  boxKey: string;
  lang: string;
  onAcceptChange: (accepted: boolean) => void;
  initialValue?: boolean;
  // file_upload variant:
  //   legacyCallback=false (default): onImageChange(file: File|null, dataUrl: string|null)
  //   legacyCallback=true:            onImageChange(uploaded: boolean, fileData: { name, type, url } | null)
  onImageChange?: (...args: any[]) => void;
  initialFile?: any;            // file_upload variant: null | sentinel-string ("auto-accepted-via-select-all-ok") | { name, type, url }
  legacyCallback?: boolean;     // file_upload variant: when true, use legacy (uploaded, fileData) signature
  enableZoom?: boolean;         // file_upload variant: when true, auto-open zoom modal on upload (2-step accept: OK in zoom = accept). Mirrors existing DiversReport UX.
  currentBookingNumber?: string;
  mode?: 'in' | 'out';
  showOkButton?: boolean;       // checkbox variant: when false, no OK button — checkbox is interactive (one-way accept)
  keepColorOnChecked?: boolean; // checkbox variant: when true, keep original color (no green) when accepted
  showWarningIcon?: boolean;    // checkbox variant: when true, render ⚠️ icon next to title
}

// Static color map — Tailwind JIT cannot resolve dynamic class names from style_color strings
const COLOR_MAP: Record<string, { border: string; accent: string; bg: string }> = {
  blue:   { border: '#2563eb', accent: '#1d4ed8', bg: '#dbeafe' },
  orange: { border: '#d97706', accent: '#c2410c', bg: '#ffedd5' },
  red:    { border: '#dc2626', accent: '#991b1b', bg: '#fee2e2' },
  green:  { border: '#16a34a', accent: '#15803d', bg: '#d1fae5' },
};

const SUCCESS_BORDER = '#22c55e';
const SUCCESS_BG     = '#d1fae5';
const BLACK          = '#000000';

function pick(box: BoxData, field: string, lang: string): string {
  return box[`${field}_${lang}`] ?? box[`${field}_en`] ?? '';
}

// Convert any initialFile value (null | sentinel string | object{url,name,type}) to internal preview-capable form
function deriveInitialFile(initialFile: any): { url: string; raw: File | null } | null {
  if (initialFile && typeof initialFile === 'object' && initialFile.url) {
    return { url: initialFile.url, raw: null };
  }
  return null; // null, undefined, or sentinel string — no preview but acceptance handled separately
}

export default function DynamicBox({
  boxKey,
  lang,
  onAcceptChange,
  initialValue = false,
  onImageChange,
  initialFile = null,
  legacyCallback = false,
  enableZoom = false,
  currentBookingNumber,
  mode = 'in',
  showOkButton = true,
  keepColorOnChecked = false,
  showWarningIcon = false,
}: DynamicBoxProps) {
  const [boxData, setBoxData] = useState<BoxData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [accepted, setAccepted] = useState(initialValue || !!initialFile);
  const consentLoadedRef = useRef(false);

  // file_upload state — initialize from initialFile (object → preview; sentinel string → no preview)
  const [file, setFile]       = useState<{ url: string; raw: File | null } | null>(() => deriveInitialFile(initialFile));
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);
  const inputRef              = useRef<HTMLInputElement | null>(null);

  // modal_warning state
  const [modalOpen, setModalOpen]                   = useState(false);
  const [modalLocalAccepted, setModalLocalAccepted] = useState(false);

  useEffect(() => {
    if (initialValue || initialFile) setAccepted(true);
  }, [initialValue, initialFile]);

  // === BOX CONSENT (DB-backed, per booking+mode, follows box_key across pages) ===
  // Source of truth = DB (box-consents.php). No localStorage fallback — wait for explicit currentBookingNumber prop.
  useEffect(() => {
    const bk = (currentBookingNumber || '').trim();
    if (!bk) return;
    consentLoadedRef.current = false; // reset lock so booking change can re-evaluate
    let cancelled = false;
    fetch(`/api/box-consents.php?booking_number=${encodeURIComponent(bk)}&mode=${encodeURIComponent(mode || 'in')}`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled || !j || !j.success || !j.consents) return;
        const c = j.consents[boxKey];
        if (c && c.accepted) {
          consentLoadedRef.current = true;
          setAccepted(true);
          onAcceptChange(true);
        } else if (!initialValue && !initialFile) {
          // No consent row for this booking AND parent doesn't force initialValue → ensure unchecked.
          setAccepted(false);
          onAcceptChange(false);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxKey, currentBookingNumber, mode]);

  const persistConsent = (val: boolean) => {
    const bk = (currentBookingNumber || '').trim();
    if (!bk) return;
    // 🟡 PREVIEW: το πρασίνισμα δουλεύει στο UI αλλά δεν αποθηκεύεται.
    if (isCheckinPreview()) { console.log('🟡 PREVIEW — skip consent save:', boxKey); return; }
    fetch('/api/box-consents.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        booking_number: bk,
        box_key: boxKey,
        mode: mode || 'in',
        accepted: val,
      }),
    }).catch(() => {});
  };

  // file_upload: sync internal file when parent updates initialFile (e.g., rehydrate from API response.diversReport, auto-accept sentinel)
  useEffect(() => {
    if (initialFile) {
      setFile(deriveInitialFile(initialFile));
    }
  }, [initialFile]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/master-boxes.php?box_key=${encodeURIComponent(boxKey)}`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        if (!j.success) {
          setError(j.error || 'Failed to load box');
        } else {
          setBoxData(j.box);
        }
      })
      .catch((e) => { if (!cancelled) setError(String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [boxKey]);

  if (loading) {
    return <div className="text-sm text-gray-500 p-2">Loading…</div>;
  }
  if (error || !boxData) {
    return <div className="text-sm text-red-600 p-2">DynamicBox error: {error || 'no data'}</div>;
  }

  // Visibility per mode (DB-driven hide toggles)
  const isHidden =
    (mode === 'in'  && (boxData.hide_checkin  === true || boxData.hide_checkin  === 't')) ||
    (mode === 'out' && (boxData.hide_checkout === true || boxData.hide_checkout === 't'));
  if (isHidden) return null;

  const colors        = COLOR_MAP[boxData.style_color] || COLOR_MAP.blue;
  const title         = pick(boxData, 'title', lang);
  const description   = pick(boxData, 'description', lang);
  const checkboxLabel = pick(boxData, 'checkbox_label', lang);
  const buttonLabel   = pick(boxData, 'button_label', lang) || 'OK';

  // === CHECKBOX VARIANT (matches MainsailAgreement style) ===
  if (boxData.box_type === 'checkbox') {
    const handleAccept = () => { setAccepted(true); onAcceptChange(true); persistConsent(true); };
    const borderColor = keepColorOnChecked
      ? colors.border
      : (accepted ? SUCCESS_BORDER : colors.border);
    const bgColor = keepColorOnChecked
      ? colors.bg
      : (accepted ? SUCCESS_BG : 'transparent');
    return (
      <div className="mb-5">
        <div className="rounded-xl p-4" style={{
          border: `2px solid ${borderColor}`,
          background: bgColor,
        }}>
          <div className="font-semibold flex items-start gap-3" style={{ color: BLACK }}>
            {showWarningIcon && <span className="text-2xl">⚠️</span>}
            <span>{title}</span>
          </div>
          {description && (
            <p className="mt-2 text-[15px] whitespace-pre-wrap" style={{ color: BLACK }}>{description}</p>
          )}
          {checkboxLabel && (
            <div className="mt-3 flex items-start gap-3">
              <input
                type="checkbox"
                checked={accepted}
                readOnly={showOkButton}
                onChange={showOkButton ? undefined : (e) => { if (e.target.checked) handleAccept(); }}
                className="mt-1"
              />
              <p className="text-[15px]" style={{ color: BLACK }}>{checkboxLabel}</p>
            </div>
          )}
          {showOkButton && (
            <div className="flex justify-end mt-3">
              <button
                type="button"
                onClick={handleAccept}
                disabled={accepted}
                className={`px-3 py-1 rounded text-sm text-white ${accepted ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={{ background: colors.border }}
              >
                {buttonLabel}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // === FILE UPLOAD VARIANT — two modes ===
  // ADMIN-GLOBAL (boxData.image_url truthy):
  //   Read-only thumbnail from DB, click → zoom modal with OK (accept) + × (close).
  //   Backdrop & × close only — no cancel/accept. OK commits accept (once-only — button hidden after).
  // CUSTOMER-UPLOAD (boxData.image_url null/missing):
  //   User picks file → thumbnail + separate accept checkbox. Click thumb → view-only zoom. × removes file.
  if (boxData.box_type === 'file_upload') {
    // --- ADMIN-GLOBAL MODE ---
    if (boxData.image_url) {
      const handleAdminAccept = () => {
        setAccepted(true);
        onAcceptChange(true);
        // Persist acceptance: admin-global image is acknowledged (not uploaded). Write a marker
        // via onImageChange so the parent saves it (e.g. page3 → page3_checklist.divers_report).
        // On reload the parent derives accepted=true from this marker (no '✗0 / Required' relapse).
        onImageChange?.(true, 'admin-accepted');
        setZoomUrl(null);
      };
      return (
        <>
          <div className="rounded-xl p-3 mb-5 mt-4" style={{
            border: `2px solid ${accepted ? SUCCESS_BORDER : colors.border}`,
            background: accepted ? SUCCESS_BG : 'transparent',
          }}>
            <div className="flex items-center justify-between">
              <span className="font-bold" style={{ color: BLACK }}>{title}</span>
              <div className="text-xs font-semibold" style={{ color: accepted ? SUCCESS_BORDER : BLACK }}>
                {accepted ? '✓ 1' : '✗ 0'}
              </div>
            </div>
            <div className="mt-2 inline-block">
              <div className="relative border rounded p-1 bg-white" style={{ borderColor: BLACK, width: '80px' }}>
                <img
                  src={boxData.image_url}
                  alt={title}
                  className="w-full h-16 object-cover rounded cursor-zoom-in"
                  onClick={() => setZoomUrl(boxData.image_url)}
                />
              </div>
            </div>
            {!accepted && checkboxLabel && (
              <div className="mt-2 text-xs" style={{ color: '#ef4444' }}>{checkboxLabel}</div>
            )}
          </div>

          {/* Zoom modal — × or backdrop = close only; OK = accept (hidden post-accept) */}
          {zoomUrl && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setZoomUrl(null)}>
              <div className="bg-white rounded-xl p-4 w-[95vw] max-w-5xl relative" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => setZoomUrl(null)}
                  className="absolute -top-3 -right-3 w-10 h-10 rounded-full border-2 bg-white text-2xl z-10 flex items-center justify-center"
                  style={{ borderColor: BLACK }}
                  aria-label="Close zoom"
                >×</button>
                <div className="mb-3 text-base font-semibold pr-12">{title}</div>
                <div className="w-full flex items-center justify-center" style={{ height: '80vh' }}>
                  <img src={zoomUrl} alt="zoom" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                </div>
                {!accepted && (
                  <div className="flex justify-end mt-3">
                    <button
                      type="button"
                      onClick={handleAdminAccept}
                      className="px-4 py-2 rounded font-semibold text-white"
                      style={{ background: colors.border }}
                    >{buttonLabel}</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      );
    }

    // --- CUSTOMER-UPLOAD MODE (existing v6.77 decoupled behavior) ---
    const handlePick = () => inputRef.current?.click();
    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      const f = e.target.files?.[0];
      if (!f) return;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = typeof reader.result === 'string' ? reader.result : null;
        if (!dataUrl) return;
        setFile({ url: dataUrl, raw: f });
        // Upload does NOT auto-accept — user toggles accept via the separate checkbox
        if (legacyCallback) {
          onImageChange?.(true, { name: f.name, type: f.type, url: dataUrl });
        } else {
          onImageChange?.(f, dataUrl);
        }
      };
      reader.readAsDataURL(f);
    };
    const handleRemove = () => {
      setFile(null);
      setAccepted(false);
      onAcceptChange(false);
      if (legacyCallback) {
        onImageChange?.(false, null);
      } else {
        onImageChange?.(null, null);
      }
      if (inputRef.current) inputRef.current.value = '';
    };
    const handleAcceptToggle: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      const next = e.target.checked;
      setAccepted(next);
      onAcceptChange(next);
    };

    return (
      <>
        <div className="rounded-xl p-3 mb-5 mt-4" style={{
          border: `2px solid ${accepted ? SUCCESS_BORDER : colors.border}`,
          background: accepted ? SUCCESS_BG : 'transparent',
        }}>
          <div className="flex items-center justify-between">
            <span className="font-bold" style={{ color: BLACK }}>{title}</span>
            <div className="text-xs font-semibold" style={{ color: accepted ? SUCCESS_BORDER : BLACK }}>
              {accepted ? '✓ 1' : '✗ 0'}
            </div>
          </div>

          {!file && (
            <div className="mt-2">
              <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleChange} />
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={handlePick}
                  className="px-3 py-1 rounded border text-xs font-semibold self-start"
                  style={{ borderColor: BLACK, color: BLACK }}
                >
                  {description || buttonLabel}
                </button>
                {checkboxLabel && (
                  <span className="text-xs" style={{ color: '#ef4444' }}>{checkboxLabel}</span>
                )}
              </div>
            </div>
          )}

          {file && (
            <div className="mt-2">
              <div className="inline-block">
                <div className="relative border rounded p-1 bg-white" style={{ borderColor: BLACK, width: '80px' }}>
                  <button
                    type="button"
                    onClick={handleRemove}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full border bg-white z-20"
                    style={{ borderColor: BLACK }}
                    aria-label="Remove image"
                  >×</button>
                  <img
                    src={file.url}
                    alt={title}
                    className={`w-full h-16 object-cover rounded ${enableZoom ? 'cursor-zoom-in' : ''}`}
                    onClick={enableZoom ? () => setZoomUrl(file.url) : undefined}
                  />
                </div>
              </div>
              <label className="mt-3 flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={handleAcceptToggle}
                  className="mt-1"
                />
                <span className="text-[15px]" style={{ color: BLACK }}>{checkboxLabel || buttonLabel}</span>
              </label>
            </div>
          )}
        </div>

        {/* Zoom modal — view-only: close via × button or backdrop click */}
        {zoomUrl && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setZoomUrl(null)}>
            <div className="bg-white rounded-xl p-4 w-[95vw] max-w-5xl relative" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setZoomUrl(null)}
                className="absolute -top-3 -right-3 w-10 h-10 rounded-full border-2 bg-white text-2xl z-10 flex items-center justify-center"
                style={{ borderColor: BLACK }}
                aria-label="Close zoom"
              >×</button>
              <div className="mb-3 text-base font-semibold pr-12">{title}</div>
              <div className="w-full flex items-center justify-center" style={{ height: '80vh' }}>
                <img src={zoomUrl} alt="zoom" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // === MODAL WARNING VARIANT (matches WarningNoticeModal style) ===
  if (boxData.box_type === 'modal_warning') {
    const handleModalAccept = () => {
      if (modalLocalAccepted) {
        setAccepted(true);
        onAcceptChange(true);
        persistConsent(true);
        setModalOpen(false);
      }
    };
    return (
      <>
        <div className="mb-5">
          <button
            type="button"
            onClick={() => { if (!accepted) setModalOpen(true); }}
            disabled={accepted}
            className="w-full text-left rounded-xl p-4 font-bold transition-all"
            style={{
              border: `2px solid ${accepted ? SUCCESS_BORDER : colors.border}`,
              background: accepted ? SUCCESS_BG : 'transparent',
              color: accepted ? BLACK : colors.accent,
              cursor: accepted ? 'default' : 'pointer',
            }}
          >
            {accepted ? `\u2713 ${buttonLabel}` : buttonLabel}
          </button>
        </div>
        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div
              className="bg-white rounded-2xl p-10 max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}
            >
              <div className="flex items-center gap-5 mb-8 pb-6 border-b-4" style={{ borderColor: colors.border }}>
                <span className="text-5xl">⚠️</span>
                <h3 className="text-2xl font-bold" style={{ color: colors.border }}>{title}</h3>
              </div>
              <div className="mb-10 leading-relaxed space-y-6 whitespace-pre-wrap text-lg" style={{ color: BLACK }}>
                {description}
              </div>
              <div className="border-t-4 pt-8" style={{ borderColor: colors.border }}>
                <label className="flex items-center gap-5 cursor-pointer mb-6 p-5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={modalLocalAccepted}
                    onChange={(e) => setModalLocalAccepted(e.target.checked)}
                    className="w-6 h-6 cursor-pointer"
                  />
                  <span className="font-bold text-lg" style={{ color: BLACK }}>{checkboxLabel}</span>
                </label>
                <button
                  type="button"
                  onClick={handleModalAccept}
                  disabled={!modalLocalAccepted}
                  className="w-full px-10 py-4 rounded-xl font-bold text-white text-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: modalLocalAccepted ? colors.border : '#9ca3af' }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return <div className="text-sm text-red-600 p-2">DynamicBox: unknown box_type &quot;{boxData.box_type}&quot;</div>;
}
